/**
 * Utility functions for BorrowerRep form logic
 */

import { Dispatch } from "@reduxjs/toolkit";
import { ShareholderDirector } from "../../../features/merchant/rep";
import {
  setRepFirstName,
  setRepLastName,
  setUserFirstName,
  setUserLastName,
  setRepEmail as setRepEmailRedux,
  setRepTitle,
  setMerchantTitle as setMerchantTitleRedux,
  setShareholderDirector,
} from "../../../features/merchant/rep";
import { CheckboxState, RepFormData } from "../types";
import { DIRECTOR_TITLE } from "../constants";

/**
 * Derives checkbox states and representative info based on form data
 * Replaces ~40 lines of duplicate logic in 3 locations
 */
export const deriveCheckboxState = (
  formData: RepFormData,
  userEmail: string
): CheckboxState => {
  const {
    authChecked,
    directorChecked,
    repFirstName,
    repLastName,
    repEmail,
    repTitle,
    userFirstName,
    userLastName,
    merchantTitle,
    shareHoldersDirectors,
  } = formData;

  const directorEmails = shareHoldersDirectors
    .map((user) => user?.email)
    .filter((email) => email && email.trim() !== "");
  const allDirectorsEmailPresent = directorEmails.length > 0;

  let derivedAuthChecked = authChecked;
  let derivedDirectorChecked = directorChecked;
  let derivedRepFirstName = repFirstName;
  let derivedRepLastName = repLastName;
  let derivedRepEmail = repEmail;
  let derivedRepTitle = repTitle;

  if (derivedAuthChecked) {
    derivedRepLastName = userLastName;
    derivedRepFirstName = userFirstName;
    derivedRepTitle = merchantTitle;
    derivedRepEmail = userEmail;
  } else if (derivedDirectorChecked && allDirectorsEmailPresent) {
    const directorInfo = shareHoldersDirectors.find(
      (user) => user.email === repEmail
    );
    if (directorInfo) {
      derivedRepLastName = directorInfo.lastName;
      derivedRepFirstName = directorInfo.firstName;
      derivedRepTitle = DIRECTOR_TITLE;
      derivedRepEmail = directorInfo.email;
    } else {
      derivedDirectorChecked = false;
      derivedRepFirstName = "";
      derivedRepLastName = "";
      derivedRepEmail = "";
      derivedRepTitle = "";
    }
  } else if (
    !derivedAuthChecked &&
    !derivedDirectorChecked &&
    repTitle === DIRECTOR_TITLE
  ) {
    derivedRepFirstName = "";
    derivedRepLastName = "";
    derivedRepEmail = "";
    derivedRepTitle = "";
  }

  return {
    authChecked: derivedAuthChecked,
    directorChecked: derivedDirectorChecked,
    repFirstName: derivedRepFirstName,
    repLastName: derivedRepLastName,
    repEmail: derivedRepEmail,
    repTitle: derivedRepTitle,
  };
};

/**
 * Hydrates representative data into Redux store
 * Uses unwrapped payload pattern for efficiency
 */
export const hydrateRepDataToRedux = (
  dispatch: Dispatch,
  data: {
    repFirstName: string;
    repLastName: string;
    userFirstName: string;
    userLastName: string;
    repEmail: string;
    repTitle: string;
    merchantTitle: string;
    shareholdersDirectors: ShareholderDirector[];
  }
) => {
  dispatch(setRepFirstName(data.repFirstName));
  dispatch(setRepLastName(data.repLastName));
  dispatch(setUserFirstName(data.userFirstName));
  dispatch(setUserLastName(data.userLastName));
  dispatch(setRepEmailRedux(data.repEmail));
  dispatch(setRepTitle(data.repTitle));
  dispatch(setMerchantTitleRedux(data.merchantTitle));
  dispatch(setShareholderDirector(data.shareholdersDirectors));
};

/**
 * Extracts director emails from shareholders/directors list
 */
export const extractDirectorEmails = (
  shareholdersDirectors: ShareholderDirector[]
): string[] => {
  return shareholdersDirectors
    .map((user) => user?.email)
    .filter((email) => email && email.trim() !== "");
};

/**
 * Checks if auth checkbox was checked based on draft data
 */
export const wasAuthCheckboxChecked = (
  initialData: RepFormData,
  userEmail: string
): boolean => {
  return (
    initialData.userFirstName !== null &&
    initialData.userLastName !== null &&
    initialData.repEmail !== null &&
    initialData.repFirstName === initialData.userFirstName &&
    initialData.repLastName === initialData.userLastName &&
    userEmail === initialData.repEmail
  );
};

/**
 * Checks if director checkbox was checked based on draft data
 */
export const wasDirectorCheckboxChecked = (
  initialData: RepFormData,
  directorEmails: string[]
): boolean => {
  return directorEmails.includes(initialData.repEmail ?? "");
};
